import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { Variant } from '@teo/shared';
import { api } from '../api/client';
import { Badge, Panel } from '../components/Panel';
import { variantLabel } from '../components/Operation';
import { useLoad, useSubmit } from '../lib/useAsync';
import { useToast } from '../lib/useToast';
import { releaseKey, requestKey } from '../lib/idempotency';
import { formatDate, formatMoney, formatNumber } from '../lib/format';
import type { Language } from '../i18n';

/** Entrada de mercancía: a Bodega (entrada rápida o curva / media curva) o entrada propia en una ubicación distribuidora. */
export function EntryPanel({ own, locationId, onDone }: { own: boolean; locationId: number | null; onDone: () => void }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage as Language;
  const { show, showError } = useToast();
  const { busy, run } = useSubmit();
  const prefix = own ? 'masterEntry' : 'r';
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<Variant[] | null>(null);
  const [variant, setVariant] = useState<Variant | null>(null);
  const [qty, setQty] = useState('1');
  const [cost, setCost] = useState('0');
  const [supplier, setSupplier] = useState('');
  const [note, setNote] = useState('');
  const [curveId, setCurveId] = useState('');
  const [factor, setFactor] = useState<'1' | '0.5'>('1');
  const [curveCost, setCurveCost] = useState('0');
  const curves = useLoad(() => (own ? Promise.resolve([]) : api.curves()), [own]);
  const movementType = own ? 'ENTRADA_PROPIA' : 'ENTRADA_BODEGA';
  const history = useLoad(() => (locationId ? api.movements({ location_id: locationId, movement_type: movementType, limit: 60 }) : Promise.resolve([])), [locationId, movementType]);
  const stock = useLoad(() => (locationId && variant ? api.inventory({ location_id: locationId, q: variant.sku, limit: 5 }) : Promise.resolve([])), [locationId, variant?.id]);
  const current = stock.data?.find((s) => s.variant_id === variant?.id)?.qty ?? 0;
  const scope = `entrada:${own ? 'propia' : 'bodega'}:${locationId}`;
  const curveScope = `entrada:curva:${locationId}`;

  const search = (value: string) => {
    setTerm(value);
    if (value.trim().length < 2) return setResults(null);
    api.variants(value.trim()).then(setResults).catch(showError);
  };

  const finish = (doc: string) => {
    show(t('entry.done', { doc }));
    void history.reload();
    void stock.reload();
    onDone();
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      if (!locationId || !variant) return show(t('entry.selectFirst'));
      try {
        const r = await api.createEntry(requestKey(scope), { location_id: locationId, lines: [{ variant_id: variant.id, qty: Number(qty), unit_cost: Number(cost) || 0 }], supplier, note }, own);
        releaseKey(scope);
        setQty('1');
        setNote('');
        finish(r.doc_no);
      } catch (err) {
        showError(err);
      }
    });
  };

  const submitCurve = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      if (!locationId || !curveId) return;
      try {
        const r = await api.createEntry(requestKey(curveScope), { location_id: locationId, curve: { curve_id: Number(curveId), factor: factor === '1' ? 1 : 0.5, unit_cost: Number(curveCost) || 0 }, supplier, note });
        releaseKey(curveScope);
        finish(r.doc_no);
      } catch (err) {
        showError(err);
      }
    });
  };

  const selectedCurve = curves.data?.find((c) => String(c.id) === curveId);
  const curveUnits = (selectedCurve?.lines.reduce((s, l) => s + l.units, 0) ?? 0) * Number(factor);
  const rows = history.data ?? [];

  return (
    <div className="g2">
      <div>
        <Panel title={t(own ? 'entry.ownTitle' : 'entry.title')} description={t(own ? 'entry.ownDescription' : 'entry.description')}>
          <form id={own ? 'masterEntryForm' : 'receiptForm'} className="formgrid" onSubmit={submit}>
            <div className="field span2">
              <label htmlFor={`${prefix}ProductSearch`}>{t('entry.search')}</label>
              <input id={`${prefix}ProductSearch`} autoComplete="off" placeholder={t('entry.searchPlaceholder')} value={term} onChange={(e) => search(e.target.value)} />
              <div className="results" id={`${prefix}ProductResults`}>
                {results?.slice(0, 30).map((v) => (
                  <div className="result" key={v.id} data-variant={v.id}>
                    <div>
                      <b>{t('ops.refName', { ref: v.ref, name: v.product_name })}</b>
                      <small>{variantLabel(v) || v.sku}</small>
                    </div>
                    <button type="button" onClick={() => { setVariant(v); setResults(null); setTerm(''); }}>
                      {t('entry.select')}
                    </button>
                  </div>
                ))}
                {results && results.length === 0 ? <div className="empty">{t('ops.notFound')}</div> : null}
              </div>
            </div>
            <div className="span2 receipt-selected" id={`${prefix}SelectedProduct`}>
              {variant ? (
                <>
                  <b>{t('ops.refName', { ref: variant.ref, name: variant.product_name })}</b>
                  <small>{variantLabel(variant) || variant.sku}</small>
                </>
              ) : (
                <>
                  <b>{t('entry.noneSelected')}</b>
                  <small>{t('entry.noneSelectedHelp')}</small>
                </>
              )}
            </div>
            <div className="field">
              <label htmlFor={`${prefix}Qty`}>{t('entry.qty')}</label>
              <input id={`${prefix}Qty`} type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor={`${prefix}Cost`}>{t('entry.unitCost')}</label>
              <input id={`${prefix}Cost`} type="number" min={0} value={cost} onChange={(e) => setCost(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor={`${prefix}Supplier`}>{t('entry.supplier')}</label>
              <input id={`${prefix}Supplier`} value={supplier} onChange={(e) => setSupplier(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor={`${prefix}Note`}>{t('entry.note')}</label>
              <input id={`${prefix}Note`} value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <div className="calcbox span2">
              <div>
                <small>{t('entry.qtyShort')}</small>
                <b>{formatNumber(Number(qty) || 0, lang)}</b>
              </div>
              <div>
                <small>{t('entry.unitCost')}</small>
                <b>{formatMoney(Number(cost) || 0, lang)}</b>
              </div>
              <div>
                <small>{t('entry.totalValue')}</small>
                <b id={`${prefix}TotalView`}>{formatMoney((Number(qty) || 0) * (Number(cost) || 0), lang)}</b>
              </div>
              <div>
                <small>{t('entry.currentStock')}</small>
                <b id={`${prefix}StockView`}>{formatNumber(current, lang)}</b>
              </div>
            </div>
            <button className="btn black span2" type="submit" id={`${prefix}Submit`} disabled={busy || !variant}>
              {t(own ? 'entry.submitOwn' : 'entry.submit')}
            </button>
          </form>
        </Panel>
        {own ? null : (
          <div style={{ marginTop: 14 }}>
            <Panel title={t('entry.curveTitle')} description={t('entry.curveDescription')}>
              <form id="curveEntryForm" className="formgrid" onSubmit={submitCurve}>
                <div className="field span2">
                  <label htmlFor="curveEntryCurve">{t('entry.curve')}</label>
                  <select id="curveEntryCurve" value={curveId} onChange={(e) => setCurveId(e.target.value)}>
                    <option value="">{t('entry.curveChoose')}</option>
                    {(curves.data ?? []).filter((c) => c.active).map((c) => (
                      <option key={c.id} value={c.id}>
                        {t('entry.curveOption', { code: c.code, name: c.name })}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="curveEntryFactor">{t('entry.factor')}</label>
                  <select id="curveEntryFactor" value={factor} onChange={(e) => setFactor(e.target.value as '1' | '0.5')}>
                    <option value="1">{t('entry.fullCurve')}</option>
                    <option value="0.5">{t('entry.halfCurve')}</option>
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="curveEntryCost">{t('entry.unitCost')}</label>
                  <input id="curveEntryCost" type="number" min={0} value={curveCost} onChange={(e) => setCurveCost(e.target.value)} />
                </div>
                <div className="help span2" id="curveEntryUnits">
                  {t('entry.curveUnits', { n: formatNumber(curveUnits, lang) })}
                </div>
                <button className="btn black span2" type="submit" id="curveEntrySubmit" disabled={busy || !curveId}>
                  {t('entry.submitCurve')}
                </button>
              </form>
            </Panel>
          </div>
        )}
      </div>
      <Panel
        title={t(own ? 'entry.ownHistory' : 'entry.history')}
        description={t('entry.historyDescription')}
        badge={<Badge tone="green">{t('entry.historyCount', { n: rows.length })}</Badge>}
      >
        <div className="receipt-history" id={own ? 'masterEntryHistory' : 'receiptHistory'}>
          {rows.length === 0 ? <div className="empty">{t('common.noData')}</div> : null}
          {rows.map((m) => (
            <div className="receipt-row" key={m.id}>
              <time>{formatDate(m.occurred_at, lang)}</time>
              <div>
                <b>{t('ops.refName', { ref: m.ref, name: m.product_name })}</b>
                <small>{[variantLabel(m), m.doc_no, m.user_name].filter(Boolean).join(' · ')}</small>
              </div>
              <div className="receipt-amount">
                <strong>{t('entry.unitsShort', { n: formatNumber(m.qty, lang) })}</strong>
                {m.unit_cost != null ? <span>{formatMoney(m.qty * m.unit_cost, lang)}</span> : null}
              </div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
